// Constants
import { TaskGroupingState } from "@ms/uno-constants/lib/local/GroupingConstants";
import { CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { BasicPlanTaskCardPlaceHolders } from "../../../../constants/StyleConstants";
// Controls
import { PlanTaskCollectionView } from "../PlanTaskCollectionView";
import { TaskBoardView } from "../../../presentational/boardView/TaskBoardView";
import { UserContainer } from "../../../presentational/user/UserContainer";
import { LabelTag } from "../../../presentational/labelTag/LabelTag";
import { AddTaskCard } from "../../../presentational/addTaskCard/AddTaskCard";
import { TaskBoardViewPlaceHolder } from "../../../presentational/uxPlaceHolders/TaskBoardViewPlaceHolder";
// Controls Utilities
import { generateAssigneeViewColumns, generateBucketViewColumns, generateCategoryViewColumns, generateDueDateViewColumns, generatePriorityViewColumns, generateProgressViewColumns, generateProjectManagerWorkflowStateColumns } from "../../../../utilities/taskBoard/TaskBoardColumnUtilities";
// Models
import { ViewContext } from "@ms/uno-models/lib/local/contexts/IViewContext";
import { Task } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Task";
import { Bucket } from "@ms/uno-models/lib/local/client/legacyPlanner/bucket/Bucket";
import { BucketTaskboardTaskFormatting } from "@ms/uno-models/lib/local/client/legacyPlanner/task/taskFormatting/BucketTaskboardTaskFormatting";
import { TaskboardTaskFormattingCollection } from "@ms/uno-models/lib/local/client/legacyPlanner/task/taskFormatting/TaskboardTaskFormattingCollection";
import { ProgressTaskboardTaskFormatting } from "@ms/uno-models/lib/local/client/legacyPlanner/task/taskFormatting/ProgressTaskboardTaskFormatting";
import { TaskEntitySet } from "@ms/uno-models/lib/local/client/legacyPlanner/task/TaskEntitySet";
import { AssignedToTaskboardTaskFormatting } from "@ms/uno-models/lib/local/client/legacyPlanner/task/taskFormatting/AssignedToTaskboardTaskFormatting";
import { Assignment } from "@ms/uno-models/lib/local/client/legacyPlanner/task/Assignment";
import { compareByOrderHintDescending } from "@ms/uno-models/lib/local/utilities/sorting/BucketSortingUtilities";
// Models Utilities
import { ConvertPriorityNameToPriority, ConvertProgressStateToPercentComplete } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
// Stores
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
// Styles
import { Icon, Persona, PersonaSize, TooltipHost } from "@fluentui/react";
import { unknownIconProps } from "../../../../icons/icons.styles";
// Strings
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Utilities
import * as React from "react";
import find from "lodash/find";
import forEach from "lodash/forEach";
import keyBy from "lodash/keyBy";
import keys from "lodash/keys";
import last from "lodash/last";
import mapValues from "lodash/mapValues";
import pull from "lodash/pull";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { SparseNumberArray } from "@ms/uno-utilities/lib/local/collections/SparseNumberArray";
import { getDefaultDueDateForCategory } from "@ms/uno-utilities/lib/local/DateUtilities";
import { LocalIdGenerator } from "@ms/uno-utilities/lib/local/LocalIdGenerator";
import { generateOrderHintBetween } from "@ms/uno-utilities/lib/local/OrdinalUtilities";
/**
 * BasicPlanTasksBoardView control that handles rendering collections of tasks in the grid style in the context of the user
 */ export class BasicPlanTasksBoardView extends PlanTaskCollectionView {
    name() {
        return "BasicPlanTasksBoardView";
    }
    storeDidUpdate() {
        super.storeDidUpdate();
        if (this.state.columnAddTaskPivotValue == null) {
            const newValue = this.generateColumnAddTaskPivotValue();
            if (newValue != null) {
                this.setState({
                    columnAddTaskPivotValue: newValue
                });
            }
        }
    }
    //#region "Lifecycle methods"
    /**
     * React lifecycle hook called after a component updates
     * @param prevProps Previous props for the component
     * @param prevState Previous state of the component
     * @override
     */ componentDidUpdate(prevProps, prevState) {
        const { viewPartitionKey } = this.props;
        if (prevProps.viewPartitionKey !== viewPartitionKey) {
            // Clear the logging flag and add task pivot value if the plan changes
            this.setState({
                columnAddTaskPivotValue: undefined
            });
        }
        super.componentDidUpdate?.(prevProps, prevState);
    }
    renderPartitionedView() {
        const { viewPartitionKey: planId, viewKey, configProvider, storeLoader, getTaskData } = this.props;
        const [appContextStore, basicBucketStore, basicTaskStore, basicPermissionsStore, basicPlanStore, userStore, viewStore] = storeLoader.loadAll([
            Store.AppContext,
            Store.BasicBucket,
            Store.BasicTask,
            Store.BasicPermissions,
            Store.BasicPlan,
            Store.User,
            Store.View
        ]);
        if (appContextStore == null || basicBucketStore == null || basicPermissionsStore == null || basicPlanStore == null || userStore == null || viewStore == null) {
            return this.renderTaskBoardViewPlaceHolder();
        }
        const currentPlan = this.getPlan(planId);
        if (currentPlan == null) {
            return this.renderTaskBoardViewPlaceHolder();
        }
        const currentTaskGroupingState = viewStore.getCurrentTaskGroupingState(viewKey) ?? TaskGroupingState.Bucket;
        const taskData = getTaskData();
        const taskboardTaskFormatData = this.getTaskboardTaskFormatData();
        const tasksFormatMap = keyBy(taskboardTaskFormatData, "id");
        // Apply the current filter
        const currentTaskEntityFilter = viewStore.getCurrentTaskFilter(viewKey);
        const filterExceptions = viewStore.getFilterExceptions();
        const filteredData = taskData.filter((task)=>{
            return Task.ApplyEntityFilter(task, currentTaskEntityFilter, filterExceptions);
        });
        const addCardControlProvider = this.generateAddCardControlProvider;
        // Generate the taskboard callbacks
        const taskBoardCallbacks = {
            valueSetter: (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
                return;
            },
            secondaryClassifier: (task)=>{
                return false;
            },
            secondaryValueSetter: (task, useSecondaryValue)=>{
                return;
            },
            secondarySectionTitleProvider: (isVisible)=>{
                return Strings.TaskBoardColumn_CompletedSectionText;
            },
            onColumnDrop: async (columnValue, sortAfter, sortBefore)=>{
                const { actionCreatorProviders } = this.props;
                let bucket = null;
                let bucketAfter = null;
                let bucketBefore = null;
                if (columnValue != null && currentTaskGroupingState === TaskGroupingState.Bucket) {
                    bucket = basicBucketStore?.getBucket(columnValue).data ?? null;
                    if (sortAfter != null) {
                        bucketAfter = basicBucketStore?.getBucket(sortAfter).data ?? null;
                    }
                    if (sortBefore != null) {
                        bucketBefore = basicBucketStore?.getBucket(sortBefore).data ?? null;
                    }
                    if (bucket == null) {
                        return;
                    }
                    // Bucket sorting in descending order
                    const updatedBucket = bucket.setProperty("orderHint", generateOrderHintBetween(bucketBefore ? bucketBefore.orderHint : null, bucketAfter ? bucketAfter.orderHint : null));
                    const bucketActionCreator = await actionCreatorProviders.basicBucket();
                    bucketActionCreator.updateBucket(bucket, updatedBucket);
                }
                return;
            },
            addCardControlProvider
        };
        let columnDefinitions = [];
        let useSecondaryClassification = false;
        let createColumnCallback;
        let enableColumnMove = false;
        let columnCreationEnabled = false;
        let createColumnPlaceholder = "";
        let createColumnButtonText = "";
        const planPermissions = currentPlan ? basicPermissionsStore?.getPlanPermissions(currentPlan) : basicPermissionsStore?.getDefaultPlanPermissions();
        // Set the appropriate delegates based on the current grouping state
        switch(currentTaskGroupingState){
            case TaskGroupingState.Bucket:
                enableColumnMove = true;
                columnDefinitions = generateBucketViewColumns(filteredData, tasksFormatMap, basicBucketStore?.getBucketsForPlan(planId).data ?? [], true, this.renderUncategorizedBucketHeaderImage, this.onUpdateBucketTitle);
                useSecondaryClassification = true;
                taskBoardCallbacks.valueSetter = this.bucketBoardValueSetter;
                taskBoardCallbacks.secondaryClassifier = this.completedTaskClassifier;
                taskBoardCallbacks.secondaryValueSetter = this.completedSectionValueSetter;
                columnCreationEnabled = planPermissions?.canAddBuckets ?? false;
                createColumnCallback = async (title)=>{
                    const { actionCreatorProviders } = this.props;
                    // finds last bucket to generate relative order hint for the new bucket
                    // bucket order is descending, last bucket is the lowest
                    let lowestBucketOrderHint = null;
                    const lastColumn = last(columnDefinitions);
                    if (lastColumn) {
                        const planBuckets = basicBucketStore?.getBucketsForPlan(planId).data;
                        const lastBucket = find(planBuckets, (bucket)=>bucket.id === lastColumn.pivotValue);
                        lowestBucketOrderHint = lastBucket ? lastBucket.orderHint : null;
                    }
                    const bucketInfo = Bucket.builder.withPropertyBag({
                        id: "",
                        title: title,
                        planId: planId,
                        orderHint: generateOrderHintBetween(null, lowestBucketOrderHint)
                    }).withLocallyUniqueId().build();
                    const bucketActionCreator = await actionCreatorProviders.basicBucket();
                    bucketActionCreator.createBucket(bucketInfo);
                };
                createColumnButtonText = Strings.TaskBoardColumn_AddNewBucket;
                createColumnPlaceholder = Strings.TaskBoardColumn_AddNewBucketPlaceHolder;
                break;
            case TaskGroupingState.Progress:
                columnDefinitions = generateProgressViewColumns(this.props.loggers.traceLogger, filteredData, tasksFormatMap, true, this.taskAccessContext);
                useSecondaryClassification = false;
                taskBoardCallbacks.valueSetter = this.progressBoardValueSetter;
                break;
            case TaskGroupingState.Assignee:
                columnDefinitions = generateAssigneeViewColumns(filteredData, tasksFormatMap, this.getContainerMembers(currentPlan.container.externalId) ?? [], false, appContextStore.getAppContext().userId ?? "", this.getUser, this.renderAssigneeHeaderImage);
                useSecondaryClassification = true;
                taskBoardCallbacks.valueSetter = this.assignedToBoardValueSetter;
                taskBoardCallbacks.secondaryClassifier = this.completedTaskClassifier;
                taskBoardCallbacks.secondaryValueSetter = this.completedSectionValueSetter;
                break;
            case TaskGroupingState.Category:
                const categories = basicPlanStore?.getPlanDetails(planId)?.categories ?? [];
                columnDefinitions = generateCategoryViewColumns(filteredData, planId, categories, true, this.onUpdateCategoryTitle, this.renderCategoryHeaderImage);
                useSecondaryClassification = true;
                taskBoardCallbacks.valueSetter = this.categoryBoardValueSetter;
                taskBoardCallbacks.secondaryClassifier = this.completedTaskClassifier;
                taskBoardCallbacks.secondaryValueSetter = this.completedSectionValueSetter;
                break;
            case TaskGroupingState.DueDate:
                columnDefinitions = generateDueDateViewColumns(filteredData, false, this.getFlights().EnableShowAllDueDateCategories);
                useSecondaryClassification = true;
                taskBoardCallbacks.valueSetter = this.dueDateBoardValueSetter;
                taskBoardCallbacks.secondaryClassifier = this.completedTaskClassifier;
                taskBoardCallbacks.secondaryValueSetter = this.completedSectionValueSetter;
                break;
            case TaskGroupingState.Priority:
                columnDefinitions = generatePriorityViewColumns(filteredData, false);
                useSecondaryClassification = true;
                taskBoardCallbacks.valueSetter = this.priorityBoardValueSetter;
                taskBoardCallbacks.secondaryClassifier = this.completedTaskClassifier;
                taskBoardCallbacks.secondaryValueSetter = this.completedSectionValueSetter;
                break;
            case TaskGroupingState.ProjectManagerWorkflowState:
                columnDefinitions = generateProjectManagerWorkflowStateColumns(taskData, (taskId)=>basicTaskStore?.getProjectManagerWorkflowState?.(taskId) ?? CopilotWorkflowUnifiedState.Unassigned, this.renderProjectManagerHeaderImage, this.getIsDropDisabledInColumn);
                taskBoardCallbacks.valueSetter = this.projectManagerWorkflowStateBoardValueSetter;
                useSecondaryClassification = false;
                break;
            // The following cases should not be encountered on an assigned to user board view
            case TaskGroupingState.None:
            case TaskGroupingState.Plan:
                this.props.loggers.traceLogger.logTrace(0x1e258285 /* tag_4jykf */ , TraceLevel.Warning, "Invalid task grouping state for assigned to user board view");
                break;
            default:
                ErrorUtilities.unreachableCase(currentTaskGroupingState, (log)=>{
                    this.props.loggers.traceLogger.logTrace(0x1e31f496 /* tag_4m5sw */ , TraceLevel.Error, log);
                });
        }
        return /*#__PURE__*/ React.createElement(ViewContext.Provider, {
            value: this.getViewContext()
        }, /*#__PURE__*/ React.createElement(TaskBoardView, {
            autoFocus: this.props.autoFocus,
            key: viewKey,
            columnCreationEnabled: columnCreationEnabled,
            createColumnCallback: createColumnCallback,
            createColumnButtonText: createColumnButtonText,
            createColumnPlaceholder: createColumnPlaceholder,
            columnDefinitions: columnDefinitions,
            deleteColumnCallback: currentTaskGroupingState === TaskGroupingState.Bucket ? this.onDeleteBucket : undefined,
            enableColumnMove: enableColumnMove,
            useSecondaryClassification: useSecondaryClassification,
            maxAssignees: configProvider().settings.clientBasicLimits.maxTaskAssignees,
            maxDisplayablePersonas: configProvider().settings.clientBasicLimits.maxDisplayablePersonasTaskCard,
            callbacks: taskBoardCallbacks,
            taskBoardCardCallbacks: this.taskBoardCardCallbacks,
            toggleAddTaskFormVisibility: this.onToggleAddTaskFormVisibility,
            onOpenAddTaskCardFromAddTaskButton: this.onOpenAddTaskCardFromAddTaskButton,
            onCloseAddTaskCard: this.onCloseAddTaskCard,
            showAddEntityCardCallback: this.showAddEntityCardCallback,
            showPlanTitleOnCard: false,
            showCopyLinkToTask: true,
            openTaskDetails: this.onOpenTaskDetailsEditor,
            fetchReferencePreviewForTask: this.fetchReferencePreviewForTask,
            currentUserId: appContextStore.getAppContext().userId ?? "",
            disableColumnChanges: !planPermissions?.canEdit,
            getTaskPermissions: this.getTaskPermissions,
            bucketVirtualization: true,
            isSlimView: this.props.isSlimView,
            canAddGuests: basicPermissionsStore?.getUserPermissions().canAddGuests ?? false,
            enableAutoScrollOnDragItem: true,
            enableDragToScroll: true,
            fetchPermissionsWithTaskContext: this.fetchPermissionsWithTaskContext,
            availableAgents: this.getAvailableAgents()
        }));
    }
    /**
     * Renders the TaskBoardViewPlaceHolder component
     */ renderTaskBoardViewPlaceHolder() {
        return /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
            numberOfTaskCardsPerColumn: BasicPlanTaskCardPlaceHolders,
            showAddTaskButtonShimmer: true
        });
    }
    /**
     * Initializes a new instance of the BasicPlanTasksBoardView
     * @param props Props for the BasicPlanTasksBoardView
     * @param [context] Context to pass onto base component
     */ constructor(props, context){
        super(props, context), /**
     * Initializes BasicPlanTasksBoardView
     */ this.initializeBoardData = ()=>{
            this.setupCallbacks();
        }, /**
     * Set up callbacks for the taskboard only once on initialization.
     * NOTE: DO NOT bind or use specific values here because they will not change when changing plan/view
     */ this.setupCallbacks = ()=>{
            const getTaskBucket = (bucketId)=>{
                const basicBucketStore = this.props.storeLoader.load(Store.BasicBucket);
                return bucketId != null ? basicBucketStore?.getBucket(bucketId).data ?? null : null;
            };
            const getTaskDetails = (taskId)=>{
                const basicTaskStore = this.props.storeLoader.load(Store.BasicTask);
                return taskId != null ? basicTaskStore?.getTaskDetails(taskId) ?? null : null;
            };
            const getTaskContainerId = (planId)=>{
                const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
                return planId != null ? basicPlanStore?.getPlan(planId).data?.container.externalId ?? null : null;
            };
            const getTaskPlanDetails = (planId)=>{
                const basicPlanStore = this.props.storeLoader.load(Store.BasicPlan);
                return planId != null ? basicPlanStore?.getPlanDetails(planId) ?? null : null;
            };
            const getTaskPlanLabelUsageCounts = (planId)=>{
                const aggregatesStore = this.props.storeLoader.load(Store.Aggregates);
                return aggregatesStore?.getLabelUsageCountsForBasicPlan(planId) ?? new SparseNumberArray();
            };
            const getCurrentTaskGroupingState = ()=>{
                const viewStore = this.props.storeLoader.load(Store.View);
                return viewStore?.getCurrentTaskGroupingState(this.props.viewKey) ?? TaskGroupingState.Bucket;
            };
            const getCanUserModifyAssigneePool = (taskId)=>{
                const { storeLoader } = this.props;
                const [basicTaskStore, basicPlanStore] = storeLoader.loadAll([
                    Store.BasicTask,
                    Store.BasicPlan
                ]);
                if (basicPlanStore == null || basicTaskStore == null) {
                    return false;
                }
                const task = basicTaskStore.getTask(taskId).data;
                if (!task) {
                    return false;
                }
                const plan = basicPlanStore.getPlan(task.planId).data;
                return plan != null ? this.canUserAddMembers(plan) || this.canUserAccessPlanViaSharedContainerOnly(task.planId) : false;
            };
            const getDocumentPreviewThumbnail = (groupId, itemUrl)=>{
                const driveItemStore = this.props.storeLoader.load(Store.DriveItem);
                return driveItemStore?.getDriveItemThumbnail(groupId, itemUrl).data ?? null;
            };
            const toggleMyDayForTask = async (taskId)=>{
                const taskActionCreator = await this.props.actionCreatorProviders.basicTask();
                taskActionCreator.toggleMyDayForTask(taskId);
            };
            const openLink = async (attachment)=>{
                const linksAC = await this.props.actionCreatorProviders.links();
                linksAC.openAttachmentInHost(attachment);
            };
            this.taskBoardCardCallbacks = {
                getCurrentTaskGroupingState,
                getTaskBucket,
                getTaskDetails,
                getTaskGroupId: getTaskContainerId,
                getTaskPlan: this.getPlan,
                getTaskPlanDetails,
                getTaskPlanLabelUsageCounts,
                getTaskHighlightedType: this.getTaskHighlightedType,
                onDeleteTask: this.onDeleteTask,
                showAssigneeLimitError: this.onShowAssigneeLimitError,
                fetchPlanData: this.fetchPlanData,
                taskCallbacks: {
                    onRenderPersona: this.onRenderPersona,
                    onUpdateTask: this.onUpdateTask,
                    onUpdateTaskDetails: this.onUpdateTaskDetails,
                    onOpenAssignCallout: this.onOpenAssignCallout,
                    getUser: this.getUser,
                    getUserPhoto: this.getUserPhoto,
                    getBuckets: this.getBuckets,
                    getAssigneePool: this.getAssigneePool,
                    getCanUserModifyAssigneePool,
                    getCanUserAccessPlanViaSharedContainerOnly: this.canUserAccessPlanViaSharedContainerOnly,
                    onAssignTask: this.onAssignTask,
                    getDocumentPreviewThumbnail,
                    showAssigneeLimitError: this.onShowAssigneeLimitError,
                    openCopyTaskDialog: this.onCopyTaskClick,
                    openMoveTaskDialog: this.onMoveTaskClick,
                    openDeleteRecurringTaskDialog: this.onDeleteRecurringTaskClick,
                    onCopyLinkToTask: this.onCopyLinkToTask,
                    getFlights: this.getFlights,
                    getChangeGates: this.getChangeGates,
                    showInfoBanner: this.showInfoBanner,
                    fetchTaskContainerPermissions: (task)=>{
                        return; // TODO: 9210235 - Task Container Permissions
                    },
                    onOpenLink: openLink,
                    setLabelsCollapsedState: this.onSetLabelsCollapsedStateForView,
                    addUserToContainer: this.onAddUserToContainer,
                    getAssigneeSuggestions: this.onGetAssigneeSuggestion,
                    alertUser: this.alertUser,
                    getCopilotWorkflowStatusLabel: this.getCopilotWorkflowStatusLabel,
                    toggleMyDayForTask,
                    onAssignAgent: this.onAssignAgent,
                    onUnassignAgent: this.onUnassignAgent,
                    getCurrentlyAssignedAgent: this.getCurrentlyAssignedAgent
                }
            };
        }, /**
     * Initializes the columnAddTaskPivotValue to either null or a column after data is loaded
     */ this.generateColumnAddTaskPivotValue = ()=>{
            const { viewPartitionKey: planId, loggers, viewKey, getTaskData, storeLoader } = this.props;
            const [appContextStore, basicBucketStore, basicPlanStore, basicTaskStore, viewStore] = storeLoader.loadAll([
                Store.AppContext,
                Store.BasicBucket,
                Store.BasicPlan,
                Store.BasicTask,
                Store.View
            ]);
            const plan = basicPlanStore?.getPlan(planId).data;
            if (plan == null) {
                return undefined;
            }
            // Check if columnAddTaskPivotValue has never been initialized and initialize it if needed
            const newColumnAddTaskPivotValue = this.state == null ? undefined : this.state.columnAddTaskPivotValue;
            if (newColumnAddTaskPivotValue !== undefined) {
                return newColumnAddTaskPivotValue;
            }
            const taskData = getTaskData();
            // If there is task data, then we don't want to show the add task card on initial render
            if (taskData.length !== 0) {
                return null;
            }
            const taskboardTaskFormatData = this.getTaskboardTaskFormatData() ?? [];
            let columnDefinitions = [];
            const currentGroupBy = viewStore?.getCurrentTaskGroupingState(viewKey) ?? TaskGroupingState.Bucket;
            switch(currentGroupBy){
                case TaskGroupingState.Bucket:
                    columnDefinitions = generateBucketViewColumns(taskData, keyBy(taskboardTaskFormatData, "id"), basicBucketStore?.getBucketsForPlan(planId).data ?? [], true, this.renderUncategorizedBucketHeaderImage);
                    break;
                case TaskGroupingState.Progress:
                    columnDefinitions = generateProgressViewColumns(loggers.traceLogger, taskData, keyBy(taskboardTaskFormatData, "id"), true, this.taskAccessContext);
                    break;
                case TaskGroupingState.Assignee:
                    columnDefinitions = generateAssigneeViewColumns(taskData, keyBy(taskboardTaskFormatData, "id"), this.getContainerMembers(plan.container.externalId) ?? [], false, appContextStore.getAppContext().userId ?? "", this.getUser, this.renderAssigneeHeaderImage);
                    break;
                case TaskGroupingState.Category:
                    const planDetails = basicPlanStore?.getPlanDetails(planId);
                    columnDefinitions = generateCategoryViewColumns(taskData, planId, planDetails?.categories ?? [], true, this.onUpdateCategoryTitle);
                    break;
                case TaskGroupingState.DueDate:
                    columnDefinitions = generateDueDateViewColumns(taskData, false, this.getFlights().EnableShowAllDueDateCategories);
                    break;
                case TaskGroupingState.Priority:
                    columnDefinitions = generatePriorityViewColumns(taskData, false);
                    break;
                case TaskGroupingState.ProjectManagerWorkflowState:
                    columnDefinitions = generateProjectManagerWorkflowStateColumns(taskData, (taskId)=>basicTaskStore?.getProjectManagerWorkflowState?.(taskId) ?? CopilotWorkflowUnifiedState.Unassigned, this.renderProjectManagerHeaderImage, this.getIsDropDisabledInColumn);
                    break;
                // The following cases should not be encountered in a Plan View
                case TaskGroupingState.None:
                case TaskGroupingState.Plan:
                    break;
                default:
                    ErrorUtilities.unreachableCase(currentGroupBy, (log)=>{
                        loggers.traceLogger.logTrace(0x1e3193df /* tag_4mzp5 */ , TraceLevel.Error, log);
                    });
                    return;
            }
            if (columnDefinitions.length === 0) {
                return undefined;
            }
            if (currentGroupBy === TaskGroupingState.Bucket) {
                let bucketColumnIndex = 0;
                while(columnDefinitions[bucketColumnIndex].pivotValue == null){
                    bucketColumnIndex++;
                }
                if (LocalIdGenerator.isLocalTempId(columnDefinitions[bucketColumnIndex].pivotValue)) {
                    return undefined;
                } else {
                    return columnDefinitions[bucketColumnIndex].pivotValue;
                }
            } else {
                return columnDefinitions[0].pivotValue === null ? "null" : columnDefinitions[0].pivotValue?.toString();
            }
        }, /** Get task format data for the current view */ this.getTaskboardTaskFormatData = ()=>{
            const { viewKey, getTaskData, storeLoader } = this.props;
            const [basicTaskStore, viewStore] = storeLoader.loadAll([
                Store.BasicTask,
                Store.View
            ]);
            if (basicTaskStore == null || viewStore == null) {
                return [];
            }
            const currentTaskGrouping = viewStore.getCurrentTaskGroupingState(viewKey) ?? TaskGroupingState.Bucket;
            if (currentTaskGrouping !== TaskGroupingState.Assignee && currentTaskGrouping !== TaskGroupingState.Bucket && currentTaskGrouping !== TaskGroupingState.Progress) {
                // Other groupings do not use persisted formats for custom ordering
                return [];
            }
            const sortedFilteredTaskFormats = getTaskData().map((task)=>{
                const taskFormatCollection = basicTaskStore.getTaskboardTaskFormattingCollection(task.id);
                switch(currentTaskGrouping){
                    case TaskGroupingState.Assignee:
                        return taskFormatCollection?.assignmentBoardFormat;
                    case TaskGroupingState.Progress:
                        return taskFormatCollection?.progressBoardFormat;
                    case TaskGroupingState.Bucket:
                    default:
                        return taskFormatCollection?.bucketBoardFormat;
                }
            });
            // pull out task formats that are not loaded yet (undefined or null)
            pull(sortedFilteredTaskFormats, undefined, null);
            return sortedFilteredTaskFormats;
        }, /**
     * Gets the order hint value for bucket type boards on the given task and formatting map
     * @param task The task to get order hint for
     * @param tasksFormatMap Mapping with current taskboard formatting data for bucket boards
     */ this.getOrderHintForBucketBoard = (task, tasksFormatMap)=>{
            if (task && tasksFormatMap[task.id]) {
                return tasksFormatMap[task.id].orderHint;
            } else {
                return null;
            }
        }, /**
     * Gets the unassigned column order hint value for AssignedTo type boards on the given task and formatting map
     * @param task The task to get order hint for
     * @param tasksFormatMap Mapping with current taskboard formatting data for AssignedTo boards
     */ this.getOrderHintForUnassignedColumn = (task, tasksFormatMap)=>{
            if (task && tasksFormatMap[task.id]) {
                return tasksFormatMap[task.id].unassignedOrderHint;
            } else {
                return null;
            }
        }, /**
     * Gets the order hint value for given assignee column on AssignedTo type boards.
     * if order hint is not found for given assigneeValue then returns Unassigned order hint as fallback.
     * @param task The task to get order hint for
     * @param assigneeValue The assignee value to look for
     * @param tasksFormatMap Mapping with current taskboard formatting data for AssignedTo boards
     */ this.getOrderHintForAssigneeColumn = (task, assigneeValue, tasksFormatMap)=>{
            let orderHintResult = null;
            if (task && tasksFormatMap[task.id]) {
                const orderHintsByAssignee = tasksFormatMap[task.id].orderHintsByAssignee ?? {};
                orderHintResult = orderHintsByAssignee[assigneeValue];
            }
            // In case orderHint for given assignee is not found or not set we fallback to unassigned column order hint
            return orderHintResult || this.getOrderHintForUnassignedColumn(task, tasksFormatMap) || "";
        }, /**
     * Gets the order hint value for Progress type boards on the given task and formatting map
     * @param task The task to get order hint for
     * @param tasksFormatMap Mapping with current taskboard formatting data for InProgress boards
     */ this.getOrderHintForProgressBoard = (task, tasksFormatMap)=>{
            if (task && tasksFormatMap[task.id]) {
                return tasksFormatMap[task.id].orderHint;
            } else {
                return null;
            }
        }, //#region "Callbacks"
        /**
     * Get the types of highlight for the task
     * @param task Task to check the highlight type
     */ this.getTaskHighlightedType = (task)=>{
            const highlightTypeForTask = {
                assigneeHighlighted: false
            };
            return highlightTypeForTask;
        }, /**
     * Update state for AddTaskCard displaying via the add task button
     */ this.onOpenAddTaskCardFromAddTaskButton = async ()=>{
            const notificationActionCreator = await this.props.actionCreatorProviders.notification();
            notificationActionCreator.enqueueHiddenToast("SharedComponents_AddNewTaskButtonExpanded");
            this.setState({
                isAddTaskCardOpenedFromAddTaskButton: true
            });
        }, /**
     * Update state for AddTaskCard no longer displaying
     */ this.onCloseAddTaskCard = async ()=>{
            const notificationActionCreator = await this.props.actionCreatorProviders.notification();
            notificationActionCreator.enqueueHiddenToast("SharedComponents_AddNewTaskButtonCollapsed");
            this.setState({
                isAddTaskCardOpenedFromAddTaskButton: false
            });
        }, /**
     * query the status of the given column add task form visibility
     * @param pivotValue Column pivot value
     */ this.showAddEntityCardCallback = (pivotValue)=>{
            return this.state.columnAddTaskPivotValue === pivotValue;
        }, /**
     * Generate the header image for an assignee column
     * @param user IUser to generate the header image for
     */ this.renderAssigneeHeaderImage = (user)=>{
            const userStore = this.props.storeLoader.load(Store.User);
            if (user == null) {
                return null;
            }
            return /*#__PURE__*/ React.createElement(UserContainer, {
                user: user,
                onLoad: this.onRenderPersona,
                containerId: `userContainer-assigneeHeader-${user.id}`
            }, /*#__PURE__*/ React.createElement(Persona, {
                id: user.id,
                imageUrl: userStore?.getUserPhoto(user.id).data || undefined,
                size: PersonaSize.size32,
                hidePersonaDetails: true
            }));
        }, /**
     * Drag and drop value setter helper for assignedTo view board
     * @param tasksFormatMap Mapping with current taskboard formatting data for bucket boards
     * @param task The task for the card being drop
     * @param columnValue The column value that the card is dropped
     * @param sortAfter The task the card is dropped after
     * @param sortBefore The task the card is dropped before
     * @param sourceColumnValue The column value when the card came from
     */ this.assignedToBoardValueSetter = (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            const taskboardTaskFormatData = this.getTaskboardTaskFormatData();
            const tasksFormatMap = keyBy(taskboardTaskFormatData, "id");
            const updatedTask = task.getClone();
            const appContextStore = this.props.storeLoader.load(Store.AppContext);
            let anyChanges = false;
            let userRemoved = null;
            let userAdded = null;
            // Update task format data with new OrderHint
            let taskFormatData = tasksFormatMap[task.id];
            let updatedTaskFormatData;
            if (taskFormatData) {
                updatedTaskFormatData = taskFormatData.getClone();
            } else {
                // builds new empty formatting data for update
                taskFormatData = AssignedToTaskboardTaskFormatting.builder.withPropertyBag({
                    id: task.id,
                    unassignedOrderHint: undefined,
                    orderHintsByAssignee: {}
                }).build();
                // builds the baseline for update based on task
                const newAssigneeHints = {};
                let newAssigneeOrderHint = null;
                forEach(keys(task.assignments), (assignee)=>{
                    newAssigneeOrderHint = generateOrderHintBetween(newAssigneeOrderHint, null);
                    newAssigneeHints[assignee] = newAssigneeOrderHint;
                });
                updatedTaskFormatData = AssignedToTaskboardTaskFormatting.builder.withPropertyBag({
                    id: task.id,
                    unassignedOrderHint: generateOrderHintBetween(this.getOrderHintForAssigneeColumn(sortAfter, columnValue, tasksFormatMap), this.getOrderHintForAssigneeColumn(sortBefore, columnValue, tasksFormatMap)),
                    orderHintsByAssignee: newAssigneeHints
                }).build();
            }
            if (sourceColumnValue != null && sourceColumnValue !== "" && sourceColumnValue !== "null") {
                // remove the old assignee
                if (updatedTask.getAssigneeIds().indexOf(sourceColumnValue) !== -1) {
                    anyChanges = true;
                    userRemoved = sourceColumnValue;
                    delete updatedTask.assignments[sourceColumnValue];
                }
                if (updatedTaskFormatData.orderHintsByAssignee[sourceColumnValue]) {
                    delete updatedTaskFormatData.orderHintsByAssignee[sourceColumnValue];
                }
            }
            if (columnValue == null || columnValue === "" || columnValue === "null") {
                // Unassign from all
                if (updatedTask.getAssigneeIds().length > 0) {
                    anyChanges = true;
                    const keys = updatedTask.getAssigneeIds();
                    keys.forEach((key)=>{
                        delete updatedTask.assignments[key];
                        delete updatedTaskFormatData.orderHintsByAssignee[key];
                    });
                }
                // clear assignee list
                updatedTaskFormatData = updatedTaskFormatData.setProperty("orderHintsByAssignee", {});
                // sets new unassigned column order hit
                updatedTaskFormatData = updatedTaskFormatData.setProperty("unassignedOrderHint", generateOrderHintBetween(this.getOrderHintForUnassignedColumn(sortAfter, tasksFormatMap), this.getOrderHintForUnassignedColumn(sortBefore, tasksFormatMap)));
            } else {
                // Add new assignee
                if (updatedTask.getAssigneeIds().indexOf(columnValue) === -1) {
                    anyChanges = true;
                    userAdded = columnValue;
                    updatedTask.assignments[columnValue] = new Assignment(appContextStore.getAppContext().userId ?? "", new Date());
                }
                // update the order hint of the assignee
                updatedTaskFormatData.orderHintsByAssignee[columnValue] = generateOrderHintBetween(this.getOrderHintForAssigneeColumn(sortAfter, columnValue, tasksFormatMap), this.getOrderHintForAssigneeColumn(sortBefore, columnValue, tasksFormatMap));
            }
            const taskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                assignedBoardFormat: taskFormatData
            }).build();
            const updatedTaskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                assignedBoardFormat: updatedTaskFormatData
            }).build();
            if (anyChanges) {
                this.updateOnTaskDragAndDrop(task, updatedTask, taskFormatCollection, updatedTaskFormatCollection);
            } else {
                // If there is no assignment change then we still update the task format data for the order hints
                this.updateOnTaskDragAndDrop(task, task, taskFormatCollection, updatedTaskFormatCollection);
            }
            this.onAssignedToCardValueSetterUpdated(task, updatedTask, userRemoved, userAdded, columnValue, sourceColumnValue);
        }, /**
     * Callback triggered after task gets updated on assignedToBoardValueSetter.
     * @param task The original task
     * @param updatedTask The updated task
     * @param userRemoved The user id being removed or null if none
     * @param userAdded The user id being added or null if none
     * @param columnValue The string which column card is was dropped
     * @param sourceColumnValue The string which column card came from
     * @override
     */ this.onAssignedToCardValueSetterUpdated = (task, updatedTask, userRemoved, userAdded, columnValue, sourceColumnValue)=>{
        /** Nothing to do by default */ }, /**
     * Callback when a card on a board with completed section is dropped
     * @param task The task that is being dropped
     * @param [useSecondaryValue] Indicates whether the card is dropped on secondary section or not
     */ this.completedSectionValueSetter = (task, useSecondaryValue)=>{
            if (useSecondaryValue !== this.completedTaskClassifier(task)) {
                // Set state to indicate that there is a change between Active section -> Completed Section only if it is changing between sections
                this.setState({
                    isCardChangingCompletedSection: true
                });
            }
        }, /**
     * Drag and drop value setter helper for priority view board
     * @param task The task for the card being drop
     * @param columnValue The column value that the card is dropped
     * @param sortAfter The task the card is dropped after
     * @param sortBefore The task the card is dropped before
     * @param sourceColumnValue The column value when the card came from
     */ this.priorityBoardValueSetter = (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            const updatedTask = task.setProperty("priority", ConvertPriorityNameToPriority(columnValue));
            this.updateOnTaskDragAndDrop(task, updatedTask);
        }, /**
     * Drag and drop value setter helper for dueDate view board
     * @param task The task for the card being drop
     * @param columnValue The column value that the card is dropped
     * @param sortAfter The task the card is dropped after
     * @param sortBefore The task the card is dropped before
     * @param sourceColumnValue The column value when the card came from
     */ this.dueDateBoardValueSetter = (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            let updatedTask = task.getClone();
            // If both column values are same then don't update the due date
            if (columnValue?.toString() !== sourceColumnValue?.toString()) {
                const newDueDate = getDefaultDueDateForCategory(columnValue);
                if (newDueDate == null && task.recurrence?.isActive()) {
                    this.logUsage(EngagementTelemetryConstants.RecurrenceCanceledDueDate);
                }
                updatedTask = task.setDueDate(newDueDate);
            }
            this.updateOnTaskDragAndDrop(task, updatedTask);
        }, /**
     * Drag and drop value setter helper for category view board
     * @param task The task for the card being drop
     * @param columnValue The column value that the card is dropped
     * @param sortAfter The task the card is dropped after
     * @param sortBefore The task the card is dropped before
     * @param sourceColumnValue The column value when the card came from
     */ this.categoryBoardValueSetter = (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            let updatedTask = task.getClone();
            let anyChanges = false;
            if (sourceColumnValue != null && sourceColumnValue !== "null") {
                const sourceColumnNumber = parseInt(sourceColumnValue, 10);
                // remove the old category
                const appliedCategoryIndex = updatedTask.appliedCategories.indexOf(sourceColumnNumber);
                if (appliedCategoryIndex !== -1) {
                    anyChanges = true;
                    updatedTask.appliedCategories.splice(appliedCategoryIndex, 1);
                }
            }
            if (columnValue == null || columnValue === "null") {
                // Remove all categories
                if (updatedTask.appliedCategories.length > 0) {
                    anyChanges = true;
                    updatedTask = updatedTask.setProperty("appliedCategories", []);
                }
            } else {
                // Add new category
                const columnNumber = parseInt(columnValue, 10);
                if (updatedTask.appliedCategories.indexOf(columnNumber) === -1) {
                    anyChanges = true;
                    updatedTask.appliedCategories.push(columnNumber);
                }
            }
            if (anyChanges) {
                this.updateOnTaskDragAndDrop(task, updatedTask);
            }
        }, /**
     * Drag and drop value setter helper for bucket view board
     * @param tasksFormatMap Mapping with current taskboard formatting data for bucket boards
     * @param task The task for the card being drop
     * @param columnValue The column value that the card is dropped
     * @param sortAfter The task the card is dropped after
     * @param sortBefore The task the card is dropped before
     * @param sourceColumnValue The column value when the card came from
     */ this.bucketBoardValueSetter = (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            const taskboardTaskFormatData = this.getTaskboardTaskFormatData();
            const tasksFormatMap = keyBy(taskboardTaskFormatData, "id");
            const updatedTask = task.setProperty("bucketId", columnValue);
            // Update task format data with new OrderHint
            const orderHintForTaskAfter = this.getOrderHintForBucketBoard(sortAfter, tasksFormatMap);
            const orderHintForTaskBefore = this.getOrderHintForBucketBoard(sortBefore, tasksFormatMap);
            const newOrderHint = generateOrderHintBetween(orderHintForTaskAfter, orderHintForTaskBefore);
            let taskFormatData = tasksFormatMap[task.id];
            if (!taskFormatData) {
                // if format data is null then build one with empty orderHint to be updated
                taskFormatData = BucketTaskboardTaskFormatting.builder.withPropertyBag({
                    id: task.id,
                    orderHint: undefined
                }).build();
            }
            const updatedTaskFormatData = taskFormatData.setProperty("orderHint", newOrderHint);
            const taskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                bucketBoardFormat: taskFormatData
            }).build();
            const updatedTaskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                bucketBoardFormat: updatedTaskFormatData
            }).build();
            this.updateOnTaskDragAndDrop(task, updatedTask, taskFormatCollection, updatedTaskFormatCollection);
        }, /**
     * Drag and drop value setter helper for progress view board
     * @param tasksFormatMap Mapping with current taskboard formatting data for InProgress boards
     * @param task The task for the card being drop
     * @param columnValue The column value that the card is dropped
     * @param sortAfter The task the card is dropped after
     * @param sortBefore The task the card is dropped before
     * @param sourceColumnValue The column value when the card came from
     */ this.progressBoardValueSetter = (task, columnValue, sortAfter, sortBefore, sourceColumnValue)=>{
            const taskboardTaskFormatData = this.getTaskboardTaskFormatData();
            const tasksFormatMap = keyBy(taskboardTaskFormatData, "id");
            const updatedTask = task.setProperty("percentComplete", ConvertProgressStateToPercentComplete(columnValue));
            // Update task format data with new OrderHint
            const orderHintForTaskAfter = this.getOrderHintForProgressBoard(sortAfter, tasksFormatMap);
            const orderHintForTaskBefore = this.getOrderHintForProgressBoard(sortBefore, tasksFormatMap);
            const newOrderHint = generateOrderHintBetween(orderHintForTaskAfter, orderHintForTaskBefore);
            let taskFormatData = tasksFormatMap[task.id];
            if (!taskFormatData) {
                // if format data is null then build one with empty orderHint to be updated
                taskFormatData = ProgressTaskboardTaskFormatting.builder.withPropertyBag({
                    id: task.id,
                    orderHint: undefined
                }).build();
            }
            const updatedTaskFormatData = taskFormatData.setProperty("orderHint", newOrderHint);
            const taskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                progressBoardFormat: taskFormatData
            }).build();
            const updatedTaskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                progressBoardFormat: updatedTaskFormatData
            }).build();
            this.updateOnTaskDragAndDrop(task, updatedTask, taskFormatCollection, updatedTaskFormatCollection);
        }, /**
     * Handler for task update by drag and drop.
     * @param originalTask Original task that was dragged
     * @param updatedTask Task data to set based on where the task was dropped
     * @param originalTaskFormatCollection Original task format data
     * @param updatedTaskFormatCollection Task format data data to be updated
     */ this.updateOnTaskDragAndDrop = async (originalTask, updatedTask, originalTaskFormatCollection, updatedTaskFormatCollection)=>{
            const { actionCreatorProviders } = this.props;
            const { isCardChangingCompletedSection } = this.state;
            // When switching between completed and active section we also update the percentComplete state
            if (isCardChangingCompletedSection) {
                updatedTask = updatedTask.toggleCompletionState();
            }
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.updateTask(TaskEntitySet.builder().withTask(originalTask).withFormatData(originalTaskFormatCollection).build(), TaskEntitySet.builder().withTask(updatedTask).withFormatData(updatedTaskFormatCollection).build());
            if (isCardChangingCompletedSection) {
                this.setState({
                    isCardChangingCompletedSection: false
                });
            }
        }, /**
     * Toggle current column add task visibility and close other column add task form
     * | Scenario | Data | No data |
     * | Click x (dismiss btn) | Close | Close |
     * | Hit esc | Close | Close |
     * | Click other + btn | Close | Close |
     * | Click outside form | Don't close | Close |
     * | Click same + btn | Don't close | Close (toggle) |
     * @param [pivotValue] Optional parameter for the current column pivot value
     */ this.onToggleAddTaskFormVisibility = (pivotValue)=>{
            if (this.state.columnAddTaskPivotValue === pivotValue || pivotValue == null) {
                // Pivot value is null when background is clicked on
                // Pivot value is the same when toggling the current add task card
                this.setState({
                    columnAddTaskPivotValue: null
                });
            } else {
                // When opening a different column's bucket want to close any open add card even if it is dirty
                this.setState({
                    columnAddTaskPivotValue: pivotValue
                });
            }
        }, /**
     * Create an add task card control provider with the current information
     */ this.generateAddCardControlProvider = (columnValue, sortAfter, sortBefore, valueSetter, toggleAddCardControl)=>{
            const { isAddTaskCardOpenedFromAddTaskButton } = this.state;
            const { viewKey, viewPartitionKey, configProvider, renderSmartTextField, storeLoader } = this.props;
            const [basicPlanStore, basicBucketStore, basicPermissionsStore, viewStore, appContextStore] = storeLoader.loadAll([
                Store.BasicPlan,
                Store.BasicBucket,
                Store.BasicPermissions,
                Store.View,
                Store.AppContext
            ]);
            const plan = basicPlanStore?.getPlan(viewPartitionKey).data;
            if (plan == null) {
                return null;
            }
            const currentTaskGroupingState = viewStore?.getCurrentTaskGroupingState(viewKey) ?? TaskGroupingState.Bucket;
            const onAddTask = (task)=>{
                this.onCreateTask(task, columnValue, sortAfter, sortBefore);
            };
            const containerId = plan.container.externalId;
            const canUserAddMembers = this.canUserAddMembers(plan);
            const canUserAccessPlanViaSharedContainerOnly = this.canUserAccessPlanViaSharedContainerOnly(plan.id);
            const canUserAssignTasks = canUserAddMembers || canUserAccessPlanViaSharedContainerOnly;
            const buckets = basicBucketStore?.getBucketsForPlan(viewPartitionKey).data ?? [];
            // Buckets sorting is descending order
            buckets.sort(compareByOrderHintDescending);
            return /*#__PURE__*/ React.createElement(AddTaskCard, {
                autoFocus: isAddTaskCardOpenedFromAddTaskButton,
                createTask: onAddTask,
                planId: viewPartitionKey,
                toggleAddCard: toggleAddCardControl,
                closeOnKeyPress: false,
                canAddUser: canUserAddMembers,
                canAccessPlanViaSharedContainerOnly: canUserAccessPlanViaSharedContainerOnly,
                buckets: buckets,
                defaultDueDate: undefined,
                hideSetAssignment: currentTaskGroupingState === TaskGroupingState.Assignee,
                hideSetBucket: currentTaskGroupingState === TaskGroupingState.Bucket,
                hideSetDueDate: currentTaskGroupingState === TaskGroupingState.DueDate,
                getUser: this.getUser,
                getUserPhoto: this.getUserPhoto,
                getUsersByKeyword: canUserAssignTasks ? this.getUsersByKeyword : undefined,
                getAssigneePool: ()=>containerId ? this.getContainerMembers(containerId) : [],
                maxAssignees: configProvider().settings.clientBasicLimits.maxTaskAssignees,
                showAssigneeLimitError: this.onShowAssigneeLimitError,
                currentUserId: appContextStore.getAppContext().userId ?? "",
                onRenderPersona: this.onRenderPersona,
                alertUser: this.alertUser,
                onOpenAssignCallout: this.onOpenAssignCallout,
                enableSuggestedAssigneeUI: configProvider().flights.EnableSuggestedAssignee,
                addUserToContainer: this.onAddUserToContainer,
                canAddGuests: basicPermissionsStore?.getUserPermissions().canAddGuests ?? false,
                getAssigneeSuggestions: this.onGetAssigneeSuggestion,
                onAssignNonMember: this.onAssignTask,
                renderSmartTextField: renderSmartTextField
            });
        }, /**
     * Get the header image for the uncategorized bucket. An info icon with tooltip
     */ this.renderUncategorizedBucketHeaderImage = ()=>{
            return /*#__PURE__*/ React.createElement(TooltipHost, {
                content: Strings.Bucket_NullBucketInfoIconText
            }, /*#__PURE__*/ React.createElement(Icon, {
                ...unknownIconProps,
                "aria-label": Strings.Bucket_NullBucketInfoIconText,
                "aria-hidden": "false"
            }));
        }, /**
     * Callback for getting assignee suggestions
     * @param planId The plan id
     * @param task The task to get assigneeSuggestions for
     */ this.onGetAssigneeSuggestion = (planId, task)=>{
            // TODO: 9210238 - Port Suggestions
            return [];
        }, /**
     * Callback to create a new task
     * @param task new task information
     * @param groupPivotValue pivot value for the group under which the task was created. Null for no grouping
     * @param sortAfter task to order the task after in the grouping
     * @param sortBefore task to order the task before in the grouping
     */ this.onCreateTask = async (task, groupPivotValue, sortAfter, sortBefore)=>{
            const { actionCreatorProviders, storeLoader, viewKey } = this.props;
            const [appContextStore, viewStore] = storeLoader.loadAll([
                Store.AppContext,
                Store.View
            ]);
            let assignedToTaskFormat;
            let bucketTaskFormat;
            let progressTaskFormat;
            const taskboardTaskFormatData = this.getTaskboardTaskFormatData();
            const tasksFormatMap = keyBy(taskboardTaskFormatData, "id");
            const currentTaskGroupingState = viewStore?.getCurrentTaskGroupingState(viewKey) ?? TaskGroupingState.Bucket;
            switch(currentTaskGroupingState){
                case TaskGroupingState.Bucket:
                    task = task.setProperty("bucketId", groupPivotValue);
                    // Generate orderHint/format data
                    bucketTaskFormat = BucketTaskboardTaskFormatting.builder.withPropertyBag({
                        id: task.id,
                        orderHint: generateOrderHintBetween(this.getOrderHintForBucketBoard(sortAfter, tasksFormatMap), this.getOrderHintForBucketBoard(sortBefore, tasksFormatMap))
                    }).build();
                    break;
                case TaskGroupingState.Assignee:
                    // Set assignment value if not on unassigned column
                    if (groupPivotValue != null) {
                        task.assignments[groupPivotValue] = new Assignment(appContextStore.getAppContext().userId ?? "", new Date());
                    }
                    // Generate orderHint/format data
                    assignedToTaskFormat = AssignedToTaskboardTaskFormatting.builder.withPropertyBag({
                        id: task.id,
                        unassignedOrderHint: generateOrderHintBetween(this.getOrderHintForUnassignedColumn(sortAfter, tasksFormatMap), this.getOrderHintForUnassignedColumn(sortBefore, tasksFormatMap)),
                        orderHintsByAssignee: mapValues(task.assignments, (a, key)=>generateOrderHintBetween(this.getOrderHintForAssigneeColumn(sortAfter, key, tasksFormatMap), this.getOrderHintForAssigneeColumn(sortBefore, key, tasksFormatMap)))
                    }).build();
                    break;
                case TaskGroupingState.Progress:
                    task = task.setProperty("percentComplete", ConvertProgressStateToPercentComplete(groupPivotValue));
                    // Generate orderHint/format data
                    progressTaskFormat = ProgressTaskboardTaskFormatting.builder.withPropertyBag({
                        id: task.id,
                        orderHint: generateOrderHintBetween(this.getOrderHintForProgressBoard(sortAfter, tasksFormatMap), this.getOrderHintForProgressBoard(sortBefore, tasksFormatMap))
                    }).build();
                    break;
                case TaskGroupingState.Category:
                    if (groupPivotValue != null) {
                        task = task.setProperty("appliedCategories", [
                            groupPivotValue
                        ]);
                    }
                    break;
                case TaskGroupingState.DueDate:
                    task = task.setDueDate(getDefaultDueDateForCategory(groupPivotValue));
                    break;
                case TaskGroupingState.Priority:
                    task = task.setProperty("priority", ConvertPriorityNameToPriority(groupPivotValue));
                    break;
                // For ProjectManagerWorkflowState, we don't need to set any additional properties on create task
                case TaskGroupingState.ProjectManagerWorkflowState:
                // The following cases should not be encountered
                // Intentional fallthrough
                case TaskGroupingState.Plan:
                case TaskGroupingState.None:
                    break;
                default:
                    ErrorUtilities.unreachableCase(currentTaskGroupingState, (log)=>{
                        this.props.loggers.traceLogger.logTrace(0x1e31f495 /* tag_4m5sv */ , TraceLevel.Error, log);
                    });
                    return;
            }
            const taskFormatCollection = TaskboardTaskFormattingCollection.builder.withPropertyBag({
                assignedBoardFormat: assignedToTaskFormat,
                bucketBoardFormat: bucketTaskFormat,
                progressBoardFormat: progressTaskFormat
            }).build();
            const taskActionCreator = await actionCreatorProviders.basicTask();
            taskActionCreator.createTask(TaskEntitySet.builder().withTask(task).withFormatData(taskFormatCollection).build());
        }, /**
     * Callback to update a bucket's title
     * @param originalBucket Original bucket to update
     * @param updatedTitle Its new title
     */ this.onUpdateBucketTitle = async (originalBucket, updatedTitle)=>{
            const { actionCreatorProviders } = this.props;
            const updatedBucket = originalBucket.setProperty("title", updatedTitle);
            const bucketActionCreator = await actionCreatorProviders.basicBucket();
            bucketActionCreator.updateBucket(originalBucket, updatedBucket);
        }, /**
     * Updates a category's title
     * @param categoryId Id of the category to update
     * @param currentPlanId ID of the current plan
     * @param newTitle New category text
     */ this.onUpdateCategoryTitle = async (categoryId, currentPlanId, newTitle)=>{
            const { storeLoader } = this.props;
            const basicPlanStore = storeLoader.load(Store.BasicPlan);
            const originalPlanDetails = basicPlanStore?.getPlanDetails(currentPlanId);
            if (!originalPlanDetails || originalPlanDetails.categories[categoryId] === newTitle) {
                // No change
                return;
            }
            const currentPlan = basicPlanStore?.getPlan(currentPlanId).data;
            if (!currentPlan) {
                return;
            }
            const categories = originalPlanDetails.categories.slice();
            categories[categoryId] = newTitle;
            const updatedPlanDetails = originalPlanDetails.setProperty("categories", categories);
            this.onUpdatePlan(currentPlan, originalPlanDetails, updatedPlanDetails);
        }, /**
     * Get the header image for the category. A square with correct color class applied
     * @param categoryNumber Number of category
     */ this.renderCategoryHeaderImage = (categoryNumber)=>{
            return /*#__PURE__*/ React.createElement(LabelTag, {
                index: categoryNumber,
                isCollapsed: false,
                title: "",
                showBorder: true
            });
        };
        this.state = {
            ...this.state,
            theme: this.props.theme,
            isCardChangingCompletedSection: false,
            columnAddTaskPivotValue: undefined,
            isAddTaskCardOpenedFromAddTaskButton: false
        };
        this.initializeBoardData();
    }
}
